import { Component, type ErrorInfo, type ReactNode } from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
  showDetails: boolean
}

// React render-error backstop. Logs to console (so the operator can copy
// the stack out of devtools when reporting), and presents a calm
// recovery panel; the original error message is hidden behind a "Show
// details" toggle so the page stays gentle by default.
export class ErrorBoundary extends Component<
  ErrorBoundaryProps, ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null, showDetails: false }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[admin-spa] uncaught render error', error, info)
  }

  handleReset = () => {
    this.setState({ error: null, showDetails: false })
    window.location.reload()
  }

  toggleDetails = () => {
    this.setState((s) => ({ showDetails: !s.showDetails }))
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children
    return (
      <div className="mx-auto max-w-xl p-12 text-center">
        <h1 className="text-2xl font-semibold text-stone-900">
          Something went wrong
        </h1>
        <p className="mt-3 text-stone-600">
          The admin console hit an unexpected error. Reload the page;
          if it keeps happening, copy the details below before reporting.
        </p>
        <button
          type="button"
          onClick={this.handleReset}
          className="btn-primary mt-6"
        >
          Reload
        </button>
        <div className="mt-6 text-left">
          <button
            type="button"
            onClick={this.toggleDetails}
            className="text-sm text-stone-500 underline hover:text-stone-700"
          >
            {this.state.showDetails ? 'Hide details' : 'Show details'}
          </button>
          {this.state.showDetails && (
            <pre
              className="mt-2 max-h-60 overflow-auto rounded-md bg-stone-100
                         p-3 text-left text-xs text-stone-800"
            >
              {String(this.state.error?.stack ?? this.state.error)}
            </pre>
          )}
        </div>
      </div>
    )
  }
}
